import React, { useEffect, useMemo, useState } from 'react';
import { Autocomplete, CircularProgress, TextField } from '@mui/material';
import { effectiveServiceCode } from '../utils/serviceCodeDisplay';
import useDebouncedValue from '../hooks/useDebouncedValue';
import {
  fetchActivePartById,
  fetchActiveServiceById,
  searchCatalogRefs
} from '../utils/catalogSearch';

function buildCatalogOptions(parts, services) {
  const o = [];
  for (const p of parts) {
    const num = String(p.part_number ?? '').trim();
    const desc = String(p.part_description ?? p.description ?? '').trim();
    o.push({
      key: `part:${p.id}`,
      valueStr: `part:${p.id}`,
      kind: 'part',
      data: p,
      label: desc ? `${num || '—'} — ${desc}` : num || '—'
    });
  }
  for (const s of services) {
    const code = effectiveServiceCode(s) || '';
    const desc = String(s.service_description ?? s.description ?? '').trim();
    o.push({
      key: `service:${s.id}`,
      valueStr: `service:${s.id}`,
      kind: 'service',
      data: s,
      label: desc ? `${code || '—'} — ${desc}` : code || '—'
    });
  }
  return o;
}

function mergePinnedCatalogOptions(options, pinned) {
  if (!pinned) return options;
  if (options.some((o) => o.key === pinned.key)) return options;
  return [pinned, ...options];
}

async function resolvePinnedCatalogOption(draft) {
  if (!draft) return null;
  const it = draft.item_type || 'part';
  if (it === 'service' && draft.service_id != null && String(draft.service_id).trim() !== '') {
    const service = await fetchActiveServiceById(draft.service_id);
    if (!service) return null;
    const code = effectiveServiceCode(service) || '';
    const desc = String(service.service_description ?? service.description ?? '').trim();
    return {
      key: `service:${service.id}`,
      valueStr: `service:${service.id}`,
      kind: 'service',
      data: service,
      label: desc ? `${code || '—'} — ${desc}` : code || '—'
    };
  }
  if (draft.part_id != null && String(draft.part_id).trim() !== '') {
    const part = await fetchActivePartById(draft.part_id);
    if (!part) return null;
    const num = String(part.part_number ?? '').trim();
    const desc = String(part.part_description ?? part.description ?? '').trim();
    return {
      key: `part:${part.id}`,
      valueStr: `part:${part.id}`,
      kind: 'part',
      data: part,
      label: desc ? `${num || '—'} — ${desc}` : num || '—'
    };
  }
  return null;
}

/**
 * Combined part/service search for QuoteNew line popout (values like part:12 / service:3).
 * @param {(valueStr: string, record: { kind: 'part'|'service', data: object }|null) => void} onSelectValue
 */
export default function QuoteLinePopoutRefAutocomplete({
  draft,
  onSelectValue,
  size = 'small'
}) {
  const [inputValue, setInputValue] = useState('');
  const [options, setOptions] = useState([]);
  const [loading, setLoading] = useState(false);
  const [pinnedOption, setPinnedOption] = useState(null);
  const debouncedInput = useDebouncedValue(inputValue, 300);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      const pinned = await resolvePinnedCatalogOption(draft);
      if (!cancelled) setPinnedOption(pinned);
    })();
    return () => {
      cancelled = true;
    };
  }, [draft?.item_type, draft?.part_id, draft?.service_id]);

  useEffect(() => {
    let cancelled = false;
    const q = debouncedInput.trim();
    if (!q) {
      setOptions(pinnedOption ? [pinnedOption] : []);
      setLoading(false);
      return undefined;
    }
    setLoading(true);
    (async () => {
      try {
        const { parts, services } = await searchCatalogRefs(q);
        if (cancelled) return;
        const built = buildCatalogOptions(parts, services);
        setOptions(mergePinnedCatalogOptions(built, pinnedOption));
      } catch (err) {
        console.error('Catalog search failed:', err);
        if (!cancelled) setOptions(pinnedOption ? [pinnedOption] : []);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [debouncedInput, pinnedOption]);

  const value = useMemo(() => {
    if (!draft) return null;
    const it = draft.item_type || 'part';
    if (it === 'service' && draft.service_id != null && String(draft.service_id).trim() !== '') {
      return options.find((x) => x.valueStr === `service:${draft.service_id}`) || pinnedOption;
    }
    if (draft.part_id != null && String(draft.part_id).trim() !== '') {
      return options.find((x) => x.valueStr === `part:${draft.part_id}`) || pinnedOption;
    }
    return null;
  }, [draft, options, pinnedOption]);

  return (
    <Autocomplete
      size={size}
      options={options}
      value={value}
      loading={loading}
      filterOptions={(opts) => opts}
      inputValue={inputValue}
      onInputChange={(_event, newInputValue, reason) => {
        if (reason === 'reset') return;
        setInputValue(newInputValue);
      }}
      onChange={(_e, v) => {
        if (!v) return;
        setPinnedOption(v);
        onSelectValue(v.valueStr, { kind: v.kind, data: v.data });
      }}
      getOptionLabel={(opt) => opt?.label || ''}
      isOptionEqualToValue={(a, b) => a?.key === b?.key}
      noOptionsText={
        loading ? 'Searching…' : inputValue.trim() ? 'No parts or services found' : 'Type to search'
      }
      renderInput={(params) => (
        <TextField
          {...params}
          label="Part / Service"
          placeholder="Search parts or services…"
          InputLabelProps={{ ...params.InputLabelProps, shrink: true }}
          InputProps={{
            ...params.InputProps,
            endAdornment: (
              <>
                {loading ? <CircularProgress color="inherit" size={16} /> : null}
                {params.InputProps.endAdornment}
              </>
            )
          }}
        />
      )}
      ListboxProps={{ style: { maxHeight: 360 } }}
    />
  );
}
